/* ─────────────────────────────────────────────────────────────
   Machine Fitness — PDP behaviour
   Vanilla JS. No framework. Hooks into Shopify forms + AJAX cart.
   ───────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  /* ── Utilities ─────────────────────────────────────────── */

  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  // Currency-aware money formatter. Shopify serves prices already converted to
  // the buyer's presentment currency; we format with the matching symbol via
  // Intl, reading the active currency from Shopify.currency.active. Never hard-
  // code the symbol — that showed converted USD/EUR amounts with a £ sign.
  const money = (cents) => {
    const code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' }).format(amount);
    } catch (e) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(amount);
      } catch (e2) {
        return amount.toFixed(2);
      }
    }
  };

  // once(el, key) returns true the first time it sees (el, key), false afterwards.
  // The key namespaces the guard so two different inits can guard the SAME element
  // without colliding (e.g. initVariantPicker and initAddToCart both guard the product form).
  // Used to make every init*() safe to re-run on shopify:section:load without
  // double-binding listeners. New elements (from a section reload) bind; old ones skip.
  const once = (el, key) => {
    if (!el || !el.dataset) return false;
    const k = key || 'default';
    const bound = (el.dataset.bound || '').split(' ').filter(Boolean);
    if (bound.indexOf(k) !== -1) return false;
    bound.push(k);
    el.dataset.bound = bound.join(' ');
    return true;
  };

  /* ── Accordions (used in buy panel + FAQ) ──────────────── */

  function initAccordions() {
    $$('[data-accordion]').forEach(group => {
      if (!once(group, 'accordions')) return;
      const exclusive = group.dataset.accordion === 'exclusive';
      $$('.acc, .item', group).forEach(acc => {
        const head = $('.acc-head, .item-q', acc);
        if (!head) return;
        head.addEventListener('click', () => {
          const open = acc.dataset.open === 'true';
          if (exclusive) {
            $$('.acc, .item', group).forEach(o => o.dataset.open = 'false');
          }
          acc.dataset.open = open ? 'false' : 'true';
        });
      });
    });
  }

  /* ── Gallery: thumbnail clicks + mobile dots ────────────── */

  function initGallery() {
    $$('[data-gallery]').forEach(gal => {
      if (!once(gal, 'gallery')) return;
      const main   = $('.gallery-main', gal);
      const thumbs = $$('.thumb', gal);
      const dots   = $$('.gallery-dots .dot', gal);
      const slides = $$('.gallery-slide', main);

      function show(i) {
        slides.forEach((s, idx) => {
          s.dataset.active = (idx === i ? 'true' : 'false');
          s.style.opacity = (idx === i ? '1' : '0');
        });
        thumbs.forEach((t, idx) => t.classList.toggle('active', idx === i));
        dots.forEach((d, idx) => d.classList.toggle('active', idx === i));
        const meta = $('.pl-meta', main);
        const counter = $('.pl-index', main);
        if (meta && slides[i]) meta.textContent = slides[i].dataset.label || '';
        if (counter) counter.textContent = String(i + 1).padStart(2, '0') + ' / ' + String(slides.length).padStart(2, '0');
      }

      thumbs.forEach((t, i) => t.addEventListener('click', () => show(i)));
      dots.forEach((d, i) => d.addEventListener('click', () => show(i)));

      // Swipe (touch) and drag (mouse) — works on mobile and desktop
      let startX = null;
      function swipeStart(x) { startX = x; }
      function swipeEnd(x) {
        if (startX === null) return;
        const dx = x - startX;
        const current = slides.findIndex(s => s.dataset.active === 'true');
        if (dx < -40 && current < slides.length - 1) show(current + 1);
        if (dx >  40 && current > 0) show(current - 1);
        startX = null;
      }
      main.addEventListener('touchstart', e => swipeStart(e.touches[0].clientX), { passive: true });
      main.addEventListener('touchend',   e => swipeEnd(e.changedTouches[0].clientX));
      main.addEventListener('mousedown',  e => { e.preventDefault(); swipeStart(e.clientX); });
      main.addEventListener('mouseup',    e => swipeEnd(e.clientX));
      main.addEventListener('mouseleave', () => { startX = null; });
      main.style.cursor = 'grab';
    });
  }

  /* ── Sticky ATC reveal on scroll past buy module ────────── */

  function initStickyAtc() {
    const bar = $('[data-sticky-atc]');
    const buy = $('[data-buy-module]');
    if (!bar || !buy) return;
    if (!once(bar, 'stickyatc')) return;

    const io = new IntersectionObserver(
      ([entry]) => bar.dataset.visible = entry.isIntersecting ? 'false' : 'true',
      { rootMargin: '0px 0px -90% 0px', threshold: 0 }
    );
    io.observe(buy);
  }

  /* ── Variant picker → updates form + price + URL + sticky bar ── */

  function initVariantPicker() {
    const form = $('[data-product-form]');
    if (!form) return;
    if (!once(form, 'variantpicker')) return;
    const variantInput = form.querySelector('input[name="id"]');
    const variants = window.MF_PRODUCT && window.MF_PRODUCT.variants;
    if (!variants) return;

    const selectors = $$('[data-option-input]');
    const priceEl   = $('[data-product-price]');
    const cmpEl     = $('[data-product-compare]');
    const stickyPrice = $('[data-sticky-price]');
    const stickyMeta  = $('[data-sticky-meta]');
    const submitBtn   = $('[data-product-submit]');
    const stockLine   = $('[data-stock-line]');

    function currentOptionValues() {
      // Returns an array of selected values aligned to product.options order.
      const byName = {};
      selectors.forEach(input => {
        if (input.type === 'radio' && !input.checked) return;
        byName[input.dataset.optionName] = input.value;
      });
      // Walk in the canonical order stored on the form.
      const order = (form.dataset.optionOrder || '').split('|');
      return order.map(n => byName[n] || null);
    }

    function findVariant(values) {
      return variants.find(v => v.options.every((opt, i) => opt === values[i]));
    }

    function update() {
      const vals = currentOptionValues();
      let variant = findVariant(vals);
      // Products with no real variants (e.g. accessories) render no option inputs,
      // so option-matching can't resolve a variant — fall back to the sole variant
      // so the buy button stays live.
      if (!variant && variants.length === 1) variant = variants[0];

      // Update the "— value" label beside each option name (Size — M, etc.)
      selectors.forEach(input => {
        if (input.type === 'radio' && !input.checked) return;
        const key = (input.dataset.optionName || '').toLowerCase()
          .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const picked = document.querySelector('[data-picked-' + key + ']');
        if (picked) picked.textContent = '— ' + input.value;
      });

      // Buttons reflect availability per-option (greying out OOS sizes)
      selectors.forEach(input => {
        if (input.type !== 'radio') return;
        const trial = vals.slice();
        const idx = (form.dataset.optionOrder || '').split('|').indexOf(input.dataset.optionName);
        trial[idx] = input.value;
        const v = findVariant(trial);
        const label = document.querySelector('label[for="' + input.id + '"]');
        if (label) {
          label.dataset.oos = (!v || !v.available) ? 'true' : 'false';
          label.dataset.active = input.checked ? 'true' : 'false';
        }
      });

      if (!variant) {
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Unavailable'; }
        return;
      }
      variantInput.value = variant.id;

      if (priceEl) priceEl.textContent = money(variant.price);
      if (cmpEl) {
        const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
        cmpEl.textContent = hasDiscount ? money(variant.compare_at_price) : '';
        cmpEl.style.display = hasDiscount ? '' : 'none';
      }
      if (stickyPrice) stickyPrice.textContent = money(variant.price);
      if (stickyMeta) {
        const colour = (vals[0] && form.dataset.optionOrder.split('|')[0] === 'Colour') ? vals[0] : (vals[1] || '');
        const size   = (vals[1] && form.dataset.optionOrder.split('|')[1] === 'Size') ? vals[1] : (vals[0] || '');
        stickyMeta.textContent = colour + (size ? ' · ' + size : '');
      }
      if (submitBtn) {
        submitBtn.disabled = !variant.available;
        const lbl = submitBtn.querySelector('[data-submit-label]');
        if (lbl) lbl.textContent = variant.available
          ? `Add to bag — ${money(variant.price)}`
          : 'Out of stock';
      }
      if (stockLine) {
        const lowAt = parseInt(stockLine.dataset.lowAt || '5', 10);
        const inv = (typeof variant.inventory_quantity === 'number') ? variant.inventory_quantity : null;
        if (!variant.available) {
          stockLine.dataset.state = 'oos';
          stockLine.querySelector('[data-stock-text]').textContent = 'Out of stock';
        } else if (inv !== null && inv > 0 && inv <= lowAt) {
          stockLine.dataset.state = 'low';
          stockLine.querySelector('[data-stock-text]').textContent = `Low stock — only ${inv} left`;
        } else {
          stockLine.dataset.state = 'ok';
          stockLine.querySelector('[data-stock-text]').textContent = 'In stock, ships within 24 hours';
        }
      }

      // Reflect into URL
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      } catch (e) { /* ignore */ }
    }

    selectors.forEach(input => input.addEventListener('change', update));
    update();
  }

  /* ── Add to cart (AJAX, with toast + drawer open) ──────── */

  function initAddToCart() {
    const form = $('[data-product-form]');
    if (form && once(form, 'addtocart')) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(form);
        try {
          const res = await fetch(window.Shopify ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
            method: 'POST',
            body: fd,
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
          });
          if (!res.ok) throw new Error(await res.text());
          const item = await res.json();
          toast(`Added · ${item.product_title} · ${(item.variant_options || []).join(' / ')}`);
          await refreshCartCounts();
          openCartDrawer();
        } catch (err) {
          toast('Could not add — try again');
          console.error(err);
        }
      });
    }

    // Stray quick-add buttons (kept for other surfaces, NOT the set bundle —
    // the set bundle cards no longer carry [data-quick-add]; they toggle in initSetBundle.)
    $$('[data-quick-add]').forEach(btn => {
      if (!once(btn, 'addtocart')) return;
      btn.addEventListener('click', async () => {
        const id = btn.dataset.quickAdd;
        if (!id) return;
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1 }] })
          });
          if (!res.ok) throw new Error(await res.text());
          toast(`Added · ${btn.dataset.title || 'Item'}`);
          await refreshCartCounts();
        } catch (err) { toast('Could not add — try again'); console.error(err); }
      });
    });

    // Add full set
    const addSet = $('[data-add-set]');
    if (addSet && once(addSet, 'addtocart')) {
      addSet.addEventListener('click', async () => {
        const ids = (addSet.dataset.ids || '').split(',').filter(Boolean);
        if (ids.length === 0) return;
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: ids.map(id => ({ id: parseInt(id, 10), quantity: 1 })) })
          });
          if (!res.ok) throw new Error(await res.text());
          toast(`Added · ${ids.length}-piece set`);
          await refreshCartCounts();
          openCartDrawer();
        } catch (err) { toast('Could not add — try again'); console.error(err); }
      });
    }
  }

  async function refreshCartCounts() {
    try {
      const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await r.json();
      $$('[data-cart-count]').forEach(el => el.textContent = cart.item_count);
      // Re-render the drawer body
      renderCartDrawer(cart);
      return cart;
    } catch (e) { console.error(e); }
  }

  /* ── Cart drawer ───────────────────────────────────────── */

  function openCartDrawer() {
    const drawer = $('[data-cart-drawer]');
    const scrim  = $('[data-cart-scrim]');
    if (drawer) drawer.dataset.open = 'true';
    if (scrim)  scrim.dataset.open  = 'true';
    document.body.style.overflow = 'hidden';
  }

  function closeCartDrawer() {
    const drawer = $('[data-cart-drawer]');
    const scrim  = $('[data-cart-scrim]');
    if (drawer) drawer.dataset.open = 'false';
    if (scrim)  scrim.dataset.open  = 'false';
    document.body.style.overflow = '';
  }

  function renderCartDrawer(cart) {
    const body = $('[data-cart-body]');
    const totals = $('[data-cart-totals]');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = '<div style="text-align:center;padding-top:48px;color:var(--mf-concrete);font-family:var(--mf-font-mono);font-size:12px;text-transform:uppercase;letter-spacing:.14em">Your bag is empty.</div>';
      if (totals) totals.style.display = 'none';
      return;
    }
    if (totals) totals.style.display = '';

    body.innerHTML = cart.items.map(item => `
      <div class="item" data-line-key="${item.key}">
        <div class="ph">${item.image ? `<img src="${item.image}" alt="" style="width:100%;height:100%;object-fit:cover" />` : ''}</div>
        <div>
          <div class="name">${item.product_title}</div>
          <div class="meta">${(item.options_with_values || []).map(o => o.value).join(' · ')}</div>
          <div class="qty">
            <button data-qty="dec" data-key="${item.key}">−</button>
            <span>${item.quantity}</span>
            <button data-qty="inc" data-key="${item.key}">+</button>
          </div>
        </div>
        <div class="price">${money(item.final_line_price)}</div>
      </div>
    `).join('');

    // Totals
    const subEl  = $('[data-cart-subtotal]');
    const shipEl = $('[data-cart-shipping]');
    const grandEl= $('[data-cart-grand]');
    const shipMsg= $('[data-cart-ship-msg]');
    const FREE_AT = 7000; // pence
    const ship = cart.total_price >= FREE_AT ? 0 : 500;
    if (subEl)  subEl.textContent  = money(cart.total_price);
    if (shipEl) shipEl.textContent = ship === 0 ? 'Free' : money(ship);
    if (grandEl)grandEl.textContent= money(cart.total_price + ship);
    if (shipMsg) shipMsg.textContent = cart.total_price >= FREE_AT
      ? 'Free worldwide shipping unlocked.'
      : money(FREE_AT - cart.total_price) + ' from free worldwide shipping.';

    // Bind qty
    $$('[data-qty]', body).forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const dir = btn.dataset.qty === 'inc' ? 1 : -1;
        const line = cart.items.find(i => i.key === key);
        if (!line) return;
        const nextQty = Math.max(0, line.quantity + dir);
        const r = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: nextQty })
        });
        const updated = await r.json();
        $$('[data-cart-count]').forEach(el => el.textContent = updated.item_count);
        renderCartDrawer(updated);
      });
    });
  }

  function initCartDrawer() {
    $$('[data-open-cart]').forEach(b => {
      if (!once(b, 'cartdrawer')) return;
      b.addEventListener('click', (e) => {
        e.preventDefault();
        openCartDrawer();
        refreshCartCounts();
      });
    });
    $$('[data-close-cart]').forEach(b => {
      if (!once(b, 'cartdrawer')) return;
      b.addEventListener('click', closeCartDrawer);
    });
    const scrim = $('[data-cart-scrim]');
    if (scrim && once(scrim, 'cartdrawer')) scrim.addEventListener('click', closeCartDrawer);
    refreshCartCounts();
  }

  /* ── Unit tabs (Inches ↔ Centimetres) ─────────────────── */

  function bindUnitTabs(scope) {
    if (!once(scope, 'unittabs')) return;
    const tabs  = $$('[data-fit-tab]', scope);
    if (tabs.length === 0) return;
    const table = $('[data-size-table]', scope);
    tabs.forEach(t => t.addEventListener('click', () => {
      tabs.forEach(x => x.dataset.active = 'false');
      t.dataset.active = 'true';
      if (!table) return;
      const unit = t.dataset.fitTab;
      table.dataset.unit = unit;
      $$('[data-in]', table).forEach(cell => {
        cell.textContent = unit === 'cm' ? cell.dataset.cm : cell.dataset.in;
      });
    }));
  }

  /* ── Size guide modal ──────────────────────────────────── */

  function initSizeGuide() {
    const modal = $('[data-size-guide]');
    if (modal && once(modal, 'sizeguide')) {
      $$('[data-open-size-guide]').forEach(b => {
        if (!once(b, 'sizeguide')) return;
        b.addEventListener('click', (e) => {
          e.preventDefault();
          modal.dataset.open = 'true';
        });
      });
      $$('[data-close-size-guide]', modal).forEach(b => b.addEventListener('click', () => modal.dataset.open = 'false'));
      modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.dataset.open = 'false';
      });
      bindUnitTabs(modal);
    }

    // Standalone Fit & Sizing section (and any other unit-tab group)
    $$('[data-unit-group]').forEach(bindUnitTabs);
  }

  /* ── Set bundle: running total + checkmarks ─────────────── */

  function initSetBundle() {
    const root = $('[data-set-bundle]');
    if (!root) return;
    if (!once(root, 'setbundle')) return;
    const cards = $$('[data-set-card]', root);
    const totalEl = $('[data-set-total]', root);
    const addBtn  = $('[data-add-set]');

    function update() {
      let total = 0;
      const ids = [];
      cards.forEach(c => {
        if (c.dataset.selected === 'true' && c.dataset.variantId) {
          ids.push(c.dataset.variantId);
          total += parseInt(c.dataset.variantPrice || '0', 10);
        }
      });
      if (totalEl) totalEl.textContent = money(total);
      if (addBtn) {
        addBtn.dataset.ids = ids.join(',');
        addBtn.disabled = ids.length === 0;
        const lbl = addBtn.querySelector('[data-add-set-label]');
        if (lbl) lbl.textContent = ids.length === 0
          ? 'Choose your pieces above'
          : `Add ${ids.length} to bag — ${money(total)}`;
      }
    }

    cards.forEach(c => {
      const sizeBtns = $$('.cts-size', c);
      const addPiece = $('.cts-add', c);

      sizeBtns.forEach(sb => sb.addEventListener('click', () => {
        if (sb.hasAttribute('disabled')) return;
        sizeBtns.forEach(x => x.dataset.active = 'false');
        sb.dataset.active = 'true';
        c.dataset.variantId = sb.dataset.variantId;
        c.dataset.variantPrice = sb.dataset.variantPrice;
        if (addPiece) {
          addPiece.disabled = false;
          if (c.dataset.selected !== 'true') addPiece.textContent = 'Add to set';
        }
        update();
      }));

      if (addPiece) {
        addPiece.addEventListener('click', (e) => {
          e.preventDefault();
          if (!c.dataset.variantId) return;
          c.dataset.selected = c.dataset.selected === 'true' ? 'false' : 'true';
          addPiece.dataset.on = c.dataset.selected;
          addPiece.textContent = c.dataset.selected === 'true' ? 'Added ✓' : 'Add to set';
          update();
        });
      }
    });

    update();
  }

  /* ── Toast ─────────────────────────────────────────────── */

  let toastTimer = null;
  function toast(message) {
    const t = $('[data-toast]');
    if (!t) return;
    t.querySelector('[data-toast-text]').textContent = message;
    t.dataset.visible = 'true';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.dataset.visible = 'false', 2400);
  }

  /* ── Boot ──────────────────────────────────────────────── */

  // Each init*() is idempotent via the `once()` helper: it skips elements it has
  // already bound, so re-running boot() on shopify:section:load only binds new
  // elements (e.g. the reloaded section's nodes), never re-binds old ones.

  function boot() {
    initAccordions();
    initGallery();
    initStickyAtc();
    initVariantPicker();
    initAddToCart();
    initCartDrawer();
    initSizeGuide();
    initSetBundle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
